import * as React from 'react';
import { Clock, TrendingUp, Truck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './Card';
import { Grid, Stack } from './Layout';
import { KeyValueRow } from './List';
import { Loading } from './Spinner';
import { StatCard } from './StatCard';
import { Text } from './Text';

export interface DriverPerformanceViewProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Mean minutes from assignment to completion. */
  averageResponseMinutes: number;
  completedCalls: number;
  /**
   * How many days the figures cover; the per-day average divides by it.
   * The copy says a week, which is what every caller passes.
   */
  periodDays: number;
  loading?: boolean;
}

/** Minutes up to an hour; "2h 5m" past it, and "2h" on the hour. */
export function formatMinutes(minutes: number): {
  value: string;
  unit: string;
} {
  if (minutes <= 60) {
    return { value: Math.round(minutes).toString(), unit: 'minutes' };
  }
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return { value: rest === 0 ? `${hours}h` : `${hours}h ${rest}m`, unit: '' };
}

/**
 * A driver's week in three tiles and a paragraph of what each one means.
 *
 *   <DriverPerformanceView averageResponseMinutes={42} completedCalls={18}
 *     periodDays={7} />
 *
 * The figures arrive raw and the view does the rounding, so the same minutes
 * read the same in the tile and in the summary row.
 */
export const DriverPerformanceView = React.forwardRef<
  HTMLDivElement,
  DriverPerformanceViewProps
>(function DriverPerformanceView(
  {
    averageResponseMinutes,
    completedCalls,
    periodDays,
    loading = false,
    ...props
  },
  ref
) {
  if (loading) {
    return <Loading size="xl" pad="md" />;
  }

  const response = formatMinutes(averageResponseMinutes);
  const perDay =
    completedCalls && periodDays > 0
      ? (completedCalls / periodDays).toFixed(2)
      : '0.00';

  return (
    <Stack ref={ref} gap={6} {...props}>
      <Grid cols={{ md: 3 }} gap={6}>
        <StatCard
          icon={Clock}
          label="Average Response Time"
          value={response.value}
          comparison={response.unit}
        />
        <StatCard
          icon={Truck}
          label="Completed Calls"
          value={completedCalls}
          comparison="in the last 7 days"
        />
        <Card>
          <CardHeader>
            <CardTitle className="flex">
              <TrendingUp className="size-icon-md" />
              Performance Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Stack gap={0}>
              <KeyValueRow
                density="compact"
                label="Calls per day:"
                value={perDay}
              />
              <KeyValueRow
                density="compact"
                label="Avg response:"
                value={response.value}
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Card>
        <CardHeader>
          <CardTitle>Performance Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Stack gap={4}>
            <Text>
              <Text as="strong" variant="inherit">
                Response Time:
              </Text>{' '}
              Average time from job assignment to completion. Lower times
              indicate better efficiency.
            </Text>
            <Text>
              <Text as="strong" variant="inherit">
                Completed Calls:
              </Text>{' '}
              Total number of successfully completed jobs in the last 7 days.
            </Text>
            <Text>
              <Text as="strong" variant="inherit">
                Performance Summary:
              </Text>{' '}
              Daily averages and key metrics to help assess driver efficiency
              and reliability.
            </Text>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});
